/**
 * NotificationTypeAvatar - Circular icon badge shown at the start of a notification row.
 *
 * The icon reflects what the notification *is* (a mention, something that needs
 * action, or plain information) rather than which product sent it — the product
 * is carried by the chip instead. Sizing and colours live in the styles file.
 */

import type { ElementType } from "react";
import { Box, SvgIcon, Tooltip } from "@mui/material";
import { Alert, At, Checklist, InformationOutline } from "@opengov/react-capital-assets";
import type { Notification } from "@opengov/bnp-core";
import { NotificationPriority } from "@opengov/bnp-core";
import { styles } from "./NotificationsDrawer.styles";

interface NotificationTypeAvatarProps {
  readonly notification: Pick<Notification, "type" | "priority" | "taskId">;
}

/**
 * Picks the icon and its accessible label. `urgent` is returned alongside the
 * icon rather than derived separately so the precedence stays in one place: a
 * mention reads as a mention even when it is urgent.
 *
 * Beacon sends mentions as `mentions`; legacy payloads may still use `user.mentions`.
 */
function getNotificationIcon(notification: NotificationTypeAvatarProps["notification"]): {
  Icon: ElementType;
  label: string;
  urgent: boolean;
} {
  if (notification.type === "mentions" || notification.type === "user.mentions") {
    return { Icon: At as ElementType, label: "Mentions", urgent: false };
  }
  if (notification.priority === NotificationPriority.URGENT) {
    return { Icon: Alert as ElementType, label: "Action Required", urgent: true };
  }
  if (notification.type === "action_required" || notification.taskId != null) {
    return { Icon: Checklist as ElementType, label: "Action Required", urgent: false };
  }
  return { Icon: InformationOutline as ElementType, label: "Informational", urgent: false };
}

export function NotificationTypeAvatar({ notification }: NotificationTypeAvatarProps) {
  const { Icon, label, urgent } = getNotificationIcon(notification);

  return (
    <Tooltip title={label} arrow enterNextDelay={300}>
      <Box component="span" sx={[styles.typeAvatar, urgent && styles.typeAvatarUrgent]}>
        <SvgIcon component={Icon} inheritViewBox sx={styles.typeAvatarIcon} titleAccess={label} aria-label={label} />
      </Box>
    </Tooltip>
  );
}
